import React, { useCallback, useEffect, useReducer, useState } from "react"
import { atualizarContrato, obterContrato } from "../../../services/contratoAplicativo"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ContratoAplicativoInicial } from "../models/contratoAplicativoModel"
import { ContratoAplicativoActionsTypes } from "./contratoAplicativoActions"
import { ContratoAplicativoContext } from "./ContratoAplicativoContext"
import { contratoAplicativoReducer } from "./contratoAplicativoReducer"

function parseMonetario(v: number | null | undefined): string {
    if (v == null) return ""
    return v.toString()
}

export function ContratoAplicativoContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(contratoAplicativoReducer, ContratoAplicativoInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterContrato(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar dados do contrato")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: ContratoAplicativoActionsTypes.CARREGAR,
                    payload: {
                        ...ContratoAplicativoInicial,
                        razaoSocial: response.razaoSocial ?? "",
                        nomeFantasia: response.nomeFantasia ?? "",
                        cnpj: response.cnpj ?? "",
                        nomeResponsavel: response.nomeResponsavel ?? "",
                        emailResponsavel: response.emailResponsavel ?? "",
                        endereco: response.endereco ?? "",
                        valorAdesao: parseMonetario(response.valorAdesao),
                        valorMensalidade: parseMonetario(response.valorMensalidade),
                        metaVendas: parseMonetario(response.metaVendas),
                        formaPagamentoAdesao: response.formaPagamentoAdesao ?? "",
                        idVendedor: response.idVendedor ?? null,
                        termosEPolitica: response.termosEPolitica ?? "",
                        vendedores: response.vendedores ?? [],
                        podeEditar: response.podeEditar !== false,
                    },
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar dados do contrato")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const parseVal = (s: string): number | null => {
                const n = parseFloat(s.replace(",", "."))
                return isNaN(n) ? null : n
            }
            const payload = {
                razaoSocial: dados.razaoSocial || null,
                nomeFantasia: dados.nomeFantasia || null,
                cnpj: dados.cnpj || null,
                nomeResponsavel: dados.nomeResponsavel || null,
                emailResponsavel: dados.emailResponsavel || null,
                endereco: dados.endereco || null,
                valorAdesao: dados.valorAdesao ? parseVal(dados.valorAdesao) : null,
                valorMensalidade: dados.valorMensalidade ? parseVal(dados.valorMensalidade) : null,
                metaVendas: dados.metaVendas ? parseVal(dados.metaVendas) : null,
                formaPagamentoAdesao: dados.formaPagamentoAdesao || null,
                idVendedor: dados.idVendedor,
                termosEPolitica: dados.termosEPolitica || null,
            }
            const response = await atualizarContrato(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: ContratoAplicativoActionsTypes.CARREGAR,
                payload: {
                    ...dados,
                    razaoSocial: response.razaoSocial ?? "",
                    nomeFantasia: response.nomeFantasia ?? "",
                    cnpj: response.cnpj ?? "",
                    nomeResponsavel: response.nomeResponsavel ?? "",
                    emailResponsavel: response.emailResponsavel ?? "",
                    endereco: response.endereco ?? "",
                    valorAdesao: parseMonetario(response.valorAdesao),
                    valorMensalidade: parseMonetario(response.valorMensalidade),
                    metaVendas: parseMonetario(response.metaVendas),
                    formaPagamentoAdesao: response.formaPagamentoAdesao ?? "",
                    idVendedor: response.idVendedor ?? null,
                    termosEPolitica: response.termosEPolitica ?? "",
                    vendedores: response.vendedores ?? dados.vendedores,
                    podeEditar: response.podeEditar !== false,
                },
            })
        } finally {
            setLoading(false)
        }
    }, [dados, setLoading])

    return (
        <ContratoAplicativoContext.Provider value={{ dados, dispatch, salvar, erroCarregamento }}>
            <Loading />
            {children}
        </ContratoAplicativoContext.Provider>
    )
}
